"use client";

import { useState, useEffect, useCallback } from "react";
import Sidebar, { ViewMode } from "@/components/Sidebar";
import MetricCard from "@/components/MetricCard";
import LanguageBarChart from "@/components/LanguageBarChart";
import CheckpointLineChart from "@/components/CheckpointLineChart";
import CrossModelChart from "@/components/CrossModelChart";
import LanguageHeatmap from "@/components/LanguageHeatmap";
import SampleTable from "@/components/SampleTable";
import BaselinesView from "@/components/BaselinesView";
import {
  ModelInfo, LANGUAGES, MODEL_REGISTRY,
  MetricTier, METRIC_LABELS, METRIC_DESCRIPTIONS,
  LanguageMetrics, AggregateMetrics, PRIMARY_METRIC_TIERS,
} from "@/lib/types";

interface MetaInfo {
  checkpoint?: string;
  checkpoint_name?: string;
  model_id?: string;
  dataset?: string;
  inference_time_sec?: number;
  total_audio_sec?: number;
  rtf?: number;
  timestamp?: string;
  normalization_version?: string;
}

interface MetricsData {
  [key: string]: unknown;
  __overall__: AggregateMetrics;
  __meta__: MetaInfo;
  __macro_avg__: { n_languages?: number; wer_raw?: number; wer_norm?: number; wer_numcanon?: number; cer_norm?: number };
}

interface SampleData {
  id: string;
  language: string;
  reference: string;
  hypothesis: string;
  ref_norm?: string;
  hyp_norm?: string;
  detected_language?: string;
  flags?: string[];
}

interface PaginationData {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// Compare data now has nested metric tiers per language
type CompareData = Record<string, Record<string, Record<string, Record<string, number>>>>;

export default function Dashboard() {
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<ViewMode>("overview");
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [compareData, setCompareData] = useState<CompareData>({});
  const [samples, setSamples] = useState<SampleData[]>([]);
  const [pagination, setPagination] = useState<PaginationData>({ page: 1, pageSize: 20, total: 0, totalPages: 0 });
  const [sampleLanguage, setSampleLanguage] = useState<string>("all");
  const [samplePage, setSamplePage] = useState(1);
  const [sampleSearch, setSampleSearch] = useState("");
  const [samplesLoading, setSamplesLoading] = useState(false);
  const [compareCheckpoints, setCompareCheckpoints] = useState<Record<string, string>>({});
  const [selectedMetric, setSelectedMetric] = useState<MetricTier>("wer_norm");

  useEffect(() => {
    fetch("/api/models")
      .then((r) => r.json())
      .then((data: ModelInfo[]) => {
        setModels(data);
        const firstWithCkpts = data.find((m) => m.checkpoints.length > 0);
        if (firstWithCkpts) {
          setSelectedModel(firstWithCkpts.id);
          setSelectedCheckpoint(firstWithCkpts.checkpoints[firstWithCkpts.checkpoints.length - 1]);
        }
        const defaults: Record<string, string> = {};
        for (const m of data) {
          if (m.checkpoints.length > 0) {
            defaults[m.id] = m.checkpoints[m.checkpoints.length - 1];
          }
        }
        setCompareCheckpoints(defaults);
      });
  }, []);

  useEffect(() => {
    fetch("/api/compare")
      .then((r) => r.json())
      .then(setCompareData);
  }, []);

  useEffect(() => {
    if (!selectedModel || !selectedCheckpoint) return;
    fetch(`/api/metrics?model=${selectedModel}&checkpoint=${selectedCheckpoint}`)
      .then((r) => r.json())
      .then(setMetrics);
  }, [selectedModel, selectedCheckpoint]);

  const loadSamples = useCallback(() => {
    if (!selectedModel || !selectedCheckpoint) return;
    setSamplesLoading(true);
    const langParam = sampleLanguage === "all" ? "" : `&language=${sampleLanguage}`;
    const searchParam = sampleSearch ? `&search=${encodeURIComponent(sampleSearch)}` : "";
    fetch(`/api/samples?model=${selectedModel}&checkpoint=${selectedCheckpoint}&page=${samplePage}&pageSize=20${langParam}${searchParam}`)
      .then((r) => r.json())
      .then((data) => {
        setSamples(data.samples || []);
        setPagination(data.pagination || { page: 1, pageSize: 20, total: 0, totalPages: 0 });
        setSamplesLoading(false);
      })
      .catch(() => setSamplesLoading(false));
  }, [selectedModel, selectedCheckpoint, samplePage, sampleLanguage, sampleSearch]);

  useEffect(() => {
    loadSamples();
  }, [loadSamples]);

  // Build language data for bar charts
  const languageData = metrics
    ? LANGUAGES.map((lang) => {
        const lm = metrics[lang] as LanguageMetrics | undefined;
        return {
          language: lang,
          wer_raw: lm?.wer_raw ?? 0,
          wer_norm: lm?.wer_norm ?? 0,
          wer_numcanon: lm?.wer_numcanon ?? 0,
          cer_norm: lm?.cer_norm ?? 0,
        };
      })
    : [];

  const overall = metrics?.__overall__;
  const meta = metrics?.__meta__;

  // Build checkpoint progression for heatmap/line chart — extract selected metric tier
  const ckptProgressionData: Record<string, Record<string, number>> = {};
  if (selectedModel && compareData[selectedModel]) {
    for (const [ckpt, langData] of Object.entries(compareData[selectedModel])) {
      const flat: Record<string, number> = {};
      for (const [lang, tierObj] of Object.entries(langData)) {
        flat[lang] = (tierObj as Record<string, number>)?.[selectedMetric] ?? 0;
      }
      ckptProgressionData[ckpt] = flat;
    }
  }

  // Build cross-model compare data flattened by selected metric
  const crossModelFlat: Record<string, Record<string, Record<string, number>>> = {};
  for (const [modelId, ckpts] of Object.entries(compareData)) {
    crossModelFlat[modelId] = {};
    for (const [ckpt, langData] of Object.entries(ckpts)) {
      const flat: Record<string, number> = {};
      for (const [lang, tierObj] of Object.entries(langData)) {
        flat[lang] = (tierObj as Record<string, number>)?.[selectedMetric] ?? 0;
      }
      crossModelFlat[modelId][ckpt] = flat;
    }
  }

  // Metric tier selector component
  const MetricSelector = () => (
    <div className="flex items-center gap-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-0.5">
      {PRIMARY_METRIC_TIERS.map((tier) => (
        <button
          key={tier}
          onClick={() => setSelectedMetric(tier)}
          className={`px-3 py-1.5 rounded text-[10px] transition-colors ${
            selectedMetric === tier
              ? "bg-[var(--color-card-hover)] text-[var(--color-foreground)]"
              : "text-[var(--color-muted)] hover:text-[var(--color-foreground)]"
          }`}
          title={METRIC_DESCRIPTIONS[tier]}
        >
          {METRIC_LABELS[tier]}
        </button>
      ))}
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        models={models}
        selectedModel={selectedModel}
        selectedCheckpoint={selectedCheckpoint}
        activeView={activeView}
        onSelectModel={setSelectedModel}
        onSelectCheckpoint={(ckpt) => {
          setSelectedCheckpoint(ckpt);
          setSamplePage(1);
        }}
        onViewChange={setActiveView}
      />

      <main className="flex-1 overflow-y-auto p-6">
        {!selectedModel || !selectedCheckpoint ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-[var(--color-muted)] text-sm">Select a model and checkpoint to begin.</p>
          </div>
        ) : activeView === "overview" ? (
          <div className="space-y-6 max-w-[1400px]">
            {/* Header */}
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-lg font-bold text-[var(--color-foreground)]">
                  {MODEL_REGISTRY[selectedModel] || selectedModel}
                </h2>
                <p className="text-xs text-[var(--color-muted)] mt-1">
                  {selectedCheckpoint} &middot; {(meta?.dataset as string) || "indic-asr-benchmark-6k"}
                  {meta?.normalization_version && <> &middot; norm {meta.normalization_version as string}</>}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2">
                <MetricSelector />
                <p className="text-[10px] text-[var(--color-muted)]">
                  RTF: {typeof meta?.rtf === "number" ? meta.rtf.toFixed(4) : "—"} &middot; Inference: {typeof meta?.inference_time_sec === "number" ? `${(meta.inference_time_sec / 60).toFixed(1)}m` : "—"}
                </p>
              </div>
            </div>

            {/* Metric Cards — all tiers */}
            <div className="grid grid-cols-3 gap-3">
              <MetricCard
                label="WER Raw"
                value={overall?.wer_raw ?? 0}
                subtitle="Strict fidelity"
              />
              <MetricCard
                label="WER Norm"
                value={overall?.wer_norm ?? 0}
                subtitle="Primary metric"
              />
              <MetricCard
                label="WER NumCanon"
                value={overall?.wer_numcanon ?? 0}
                subtitle="Numeric robustness"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <MetricCard
                label="Space-Norm WER"
                value={overall?.space_norm_wer ?? 0}
                subtitle="Word-level, space-forgiven"
              />
              <MetricCard
                label="MER"
                value={overall?.mer ?? 0}
                subtitle="Char-level, space-stripped"
              />
              <MetricCard
                label="CER Norm"
                value={overall?.cer_norm ?? 0}
                subtitle="Script-sensitive"
              />
            </div>

            {/* Normalization impact strip */}
            {overall && overall.wer_raw !== overall.wer_norm && (
              <div className="flex gap-3">
                <div className="flex-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg px-4 py-2.5 flex items-center justify-between">
                  <span className="text-[10px] text-[var(--color-muted)]">Raw → Norm</span>
                  <span className={`text-xs font-medium tabular-nums ${(overall.wer_raw - overall.wer_norm) > 0 ? "text-[var(--color-success)]" : "text-[var(--color-danger)]"}`}>
                    {(overall.wer_raw - overall.wer_norm) > 0 ? "-" : "+"}{Math.abs(overall.wer_raw - overall.wer_norm).toFixed(2)}%
                  </span>
                </div>
                <div className="flex-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg px-4 py-2.5 flex items-center justify-between">
                  <span className="text-[10px] text-[var(--color-muted)]">Norm → NumCanon</span>
                  <span className={`text-xs font-medium tabular-nums ${(overall.wer_norm - overall.wer_numcanon) > 0 ? "text-[var(--color-success)]" : "text-[var(--color-muted)]"}`}>
                    {(overall.wer_norm - overall.wer_numcanon) > 0 ? "-" : "+"}{Math.abs(overall.wer_norm - overall.wer_numcanon).toFixed(2)}%
                  </span>
                </div>
                <div className="flex-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg px-4 py-2.5 flex items-center justify-between">
                  <span className="text-[10px] text-[var(--color-muted)]">Norm → SNW</span>
                  <span className={`text-xs font-medium tabular-nums ${(overall.wer_norm - (overall.space_norm_wer ?? 0)) > 0 ? "text-[var(--color-success)]" : "text-[var(--color-muted)]"}`}>
                    -{Math.abs(overall.wer_norm - (overall.space_norm_wer ?? 0)).toFixed(2)}%
                  </span>
                </div>
                <div className="flex-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg px-4 py-2.5 flex items-center justify-between">
                  <span className="text-[10px] text-[var(--color-muted)]">SNW → MER</span>
                  <span className={`text-xs font-medium tabular-nums ${((overall.space_norm_wer ?? 0) - (overall.mer ?? 0)) > 0 ? "text-[var(--color-warning)]" : "text-[var(--color-muted)]"}`}>
                    -{Math.abs((overall.space_norm_wer ?? 0) - (overall.mer ?? 0)).toFixed(2)}%
                  </span>
                </div>
              </div>
            )}

            {/* Bar Charts — selected metric */}
            <div className="grid grid-cols-2 gap-4">
              <LanguageBarChart
                data={languageData}
                metric={selectedMetric}
                title={`${METRIC_LABELS[selectedMetric]} by Language`}
              />
              <LanguageBarChart
                data={languageData}
                metric={selectedMetric === "cer_norm" ? "wer_norm" : "cer_norm"}
                title={`${selectedMetric === "cer_norm" ? METRIC_LABELS.wer_norm : METRIC_LABELS.cer_norm} by Language`}
              />
            </div>

            {/* Checkpoint Progression */}
            {Object.keys(ckptProgressionData).length > 1 && (
              <CheckpointLineChart
                data={ckptProgressionData}
                languages={[...LANGUAGES]}
                title={`${METRIC_LABELS[selectedMetric]} Progression — ${MODEL_REGISTRY[selectedModel]}`}
              />
            )}

            {/* Cross-Model Comparison */}
            {Object.keys(compareCheckpoints).length > 1 && (
              <>
                <div className="flex items-center gap-4 mt-2">
                  <h3 className="text-xs font-medium text-[var(--color-foreground)] tracking-wide">
                    Cross-Model Comparison
                  </h3>
                  {models
                    .filter((m) => m.checkpoints.length > 0)
                    .map((m) => (
                      <select
                        key={m.id}
                        value={compareCheckpoints[m.id] || ""}
                        onChange={(e) =>
                          setCompareCheckpoints((prev) => ({ ...prev, [m.id]: e.target.value }))
                        }
                        className="bg-[var(--color-card)] border border-[var(--color-border)] rounded px-2 py-1 text-[10px] text-[var(--color-foreground)]"
                      >
                        {m.checkpoints.map((ckpt) => (
                          <option key={ckpt} value={ckpt}>
                            {MODEL_REGISTRY[m.id]?.split("-")[0]} — {ckpt}
                          </option>
                        ))}
                      </select>
                    ))}
                </div>
                <CrossModelChart
                  data={crossModelFlat}
                  selectedCheckpoints={compareCheckpoints}
                  title={`${METRIC_LABELS[selectedMetric]} Comparison Across Models`}
                />
              </>
            )}
          </div>
        ) : activeView === "wer-analysis" ? (
          /* WER Analysis View */
          <div className="space-y-6 max-w-[1400px]">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-lg font-bold text-[var(--color-foreground)]">
                  WER Analysis — {MODEL_REGISTRY[selectedModel] || selectedModel}
                </h2>
                <p className="text-xs text-[var(--color-muted)] mt-1">
                  Language-wise comparison across checkpoints
                </p>
              </div>
              <MetricSelector />
            </div>

            {/* Heatmap */}
            {Object.keys(ckptProgressionData).length > 0 && (
              <LanguageHeatmap data={ckptProgressionData} title={`${METRIC_LABELS[selectedMetric]} Heatmap — All Checkpoints`} />
            )}

            {/* Checkpoint Line Chart */}
            {Object.keys(ckptProgressionData).length > 1 && (
              <CheckpointLineChart
                data={ckptProgressionData}
                languages={[...LANGUAGES]}
                title={`${METRIC_LABELS[selectedMetric]} Trend by Language`}
                height={400}
              />
            )}

            {/* Sample Predictions */}
            <div>
              <div className="flex items-center gap-3 mb-3">
                <h3 className="text-xs font-medium text-[var(--color-foreground)] tracking-wide">
                  Sample Predictions — {selectedCheckpoint}
                </h3>
                <select
                  value={sampleLanguage}
                  onChange={(e) => {
                    setSampleLanguage(e.target.value);
                    setSamplePage(1);
                  }}
                  className="bg-[var(--color-card)] border border-[var(--color-border)] rounded px-2 py-1 text-[10px] text-[var(--color-foreground)]"
                >
                  <option value="all">All Languages</option>
                  {LANGUAGES.map((lang) => (
                    <option key={lang} value={lang}>
                      {lang.charAt(0).toUpperCase() + lang.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <SampleTable
                samples={samples}
                pagination={pagination}
                onPageChange={setSamplePage}
                onSearch={(q) => { setSampleSearch(q); setSamplePage(1); }}
                searchQuery={sampleSearch}
                loading={samplesLoading}
              />
            </div>

            {/* Detailed Metrics Table — all four tiers */}
            {metrics && (
              <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-5 overflow-x-auto">
                <h3 className="text-xs font-medium text-[var(--color-foreground)] tracking-wide mb-4">
                  All Metric Tiers — {selectedCheckpoint}
                </h3>
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      <th className="text-left font-normal text-[var(--color-muted)] pb-2 pr-4">Language</th>
                      <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">N</th>
                      <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">WER Raw</th>
                      <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">WER Norm</th>
                      <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">WER Num</th>
                      <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">SNW</th>
                      <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">MER</th>
                      <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">CER</th>
                      <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">Spacing</th>
                      <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">Empty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {LANGUAGES.map((lang) => {
                      const lm = metrics[lang] as LanguageMetrics | undefined;
                      if (!lm || !lm.wer_norm) return null;
                      const delta_rn = lm.normalization_delta?.raw_to_norm ?? (lm.wer_raw - lm.wer_norm);
                      const delta_nn = lm.normalization_delta?.norm_to_numcanon ?? (lm.wer_norm - lm.wer_numcanon);
                      return (
                        <tr key={lang} className="border-b border-[var(--color-border)] hover:bg-[var(--color-card-hover)] transition-colors">
                          <td className="py-2 pr-4 text-[var(--color-foreground)] capitalize">{lang}</td>
                          <td className="py-2 px-2 text-right text-[var(--color-muted)] tabular-nums">{lm.n_samples}</td>
                          <td className="py-2 px-2 text-right tabular-nums text-[var(--color-foreground)]">{lm.wer_raw.toFixed(2)}%</td>
                          <td className="py-2 px-2 text-right tabular-nums">
                            <span className={lm.wer_norm > 40 ? "text-[var(--color-danger)]" : lm.wer_norm < 20 ? "text-[var(--color-success)]" : "text-[var(--color-foreground)]"}>
                              {lm.wer_norm.toFixed(2)}%
                            </span>
                          </td>
                          <td className="py-2 px-2 text-right text-[var(--color-accent)] tabular-nums">{lm.wer_numcanon.toFixed(2)}%</td>
                          <td className="py-2 px-2 text-right tabular-nums">
                            <span className={(lm.space_norm_wer ?? 0) < lm.wer_norm * 0.85 ? "text-[var(--color-success)]" : "text-[var(--color-foreground)]"}>
                              {(lm.space_norm_wer ?? 0).toFixed(2)}%
                            </span>
                          </td>
                          <td className="py-2 px-2 text-right tabular-nums">
                            <span className={(lm.mer ?? 0) < lm.wer_norm * 0.5 ? "text-[var(--color-success)]" : "text-[var(--color-foreground)]"}>
                              {(lm.mer ?? 0).toFixed(2)}%
                            </span>
                          </td>
                          <td className="py-2 px-2 text-right text-[var(--color-accent)] tabular-nums">{lm.cer_norm.toFixed(2)}%</td>
                          <td className="py-2 px-2 text-right tabular-nums">
                            <span className={(lm.wer_norm - (lm.space_norm_wer ?? 0)) > 5 ? "text-[var(--color-warning)]" : "text-[var(--color-muted)]"}>
                              +{(lm.wer_norm - (lm.space_norm_wer ?? 0)).toFixed(1)}
                            </span>
                          </td>
                          <td className="py-2 px-2 text-right text-[var(--color-muted)] tabular-nums">{lm.empty_hypotheses ?? 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : activeView === "baselines" ? (
          <BaselinesView models={models} compareData={compareData} selectedMetric={selectedMetric} />
        ) : null}
      </main>
    </div>
  );
}
